/**
 * Representative product imagery (see public/images/products/CREDITS.md).
 *
 * Shown on product cards and pages only while a product has no photo uploaded in the admin portal —
 * an uploaded photo always takes precedence. None of these show Shawal's exact models:
 * - kind 'representative' (default): licensed stock photos of the product type → "Representative image"
 * - kind 'illustrative': AI-generated illustrations → "Illustrative image"
 * Renditions are `${file}-${width}.webp`, all cropped to 5:4.
 */
export interface RepresentativePhoto {
  file: string;
  alt: string;
  kind?: 'representative' | 'illustrative';
}

export const REPRESENTATIVE_LABEL = { representative: 'Representative image', illustrative: 'Illustrative image' } as const;

export const REPRESENTATIVE_WIDTHS = [480, 800, 1200] as const;

/** Product slug → representative photo. Products without an entry keep the "Made to order" tile. */
export const REPRESENTATIVE_PHOTOS: Record<string, RepresentativePhoto> = {
  'match-football-thermobonded': {
    file: 'match-football-thermobonded',
    alt: 'Close-up of a white and blue panelled match football on grass (representative image)',
  },
  'training-football-hand-stitched': {
    file: 'training-football-hand-stitched',
    alt: 'Classic black-and-white stitched 32-panel football on grass (representative image)',
  },
  'leather-cricket-ball': {
    file: 'leather-cricket-ball',
    alt: 'Red leather cricket ball with a raised stitched seam (representative image)',
  },
  'tennis-ball-cricket': {
    file: 'tennis-ball-cricket',
    alt: 'Yellow felt tennis ball of the type used for soft-ball cricket (representative image)',
  },
  'heavyweight-t-shirt': {
    file: 'heavyweight-t-shirt',
    alt: 'Plain white crew-neck t-shirt laid flat (representative image)',
  },
  'tech-training-hoodie': {
    file: 'tech-training-hoodie',
    alt: 'Plain hoodie on a hanger (representative image)',
  },
  'oversized-street-tee': {
    file: 'oversized-street-tee',
    alt: 'Plain black t-shirt on a hanger (representative image)',
  },
  'varsity-zip-jacket': {
    file: 'varsity-zip-jacket',
    alt: 'AI-generated illustration of a plain navy varsity zip jacket with cream sleeves and striped rib trims (illustrative image)',
    kind: 'illustrative',
  },
  'sublimation-jersey': {
    file: 'sublimation-jersey',
    alt: 'AI-generated illustration of a V-neck sports jersey with an all-over navy and olive sublimated print (illustrative image)',
    kind: 'illustrative',
  },
  'english-willow-bat-grade-1': {
    file: 'english-willow-bat-grade-1',
    alt: 'AI-generated illustration of a plain pale willow cricket bat, front and back views (illustrative image)',
    kind: 'illustrative',
  },
  'kashmir-willow-bat': {
    file: 'kashmir-willow-bat',
    alt: 'AI-generated illustration of a plain reddish tan-brown willow cricket bat, front and back views (illustrative image)',
    kind: 'illustrative',
  },
  'leather-jackets': {
    file: 'leather-jacket-brown',
    alt: 'Man wearing a brown leather jacket with a stand collar and zip front (representative image)',
  },
  'leather-duffel-bags': {
    file: 'leather-duffel-bag',
    alt: 'Black leather holdall duffel bag with twin top handles and a front pocket (representative image)',
  },
  'leather-backpacks': {
    file: 'leather-backpack',
    alt: 'Man carrying a navy textured leather backpack with a zip front pocket (representative image)',
  },
  'leather-card-holders-accessories': {
    file: 'leather-card-holder-keyfob',
    alt: 'Brown leather card holder, bifold wallet and key fob set (representative image)',
  },
};

export function representativeSrc(file: string, width: number) {
  return `/images/products/${file}-${width}.webp`;
}

export function representativeSrcSet(file: string) {
  return REPRESENTATIVE_WIDTHS.map((w) => `${representativeSrc(file, w)} ${w}w`).join(', ');
}
